import { buildAddendumChanges } from '@/lib/addendumFields'
import type { ServiceAmendContract } from './ServiceDetailsShell'
import type { Addendum, AddendumChange } from '@/lib/schemas/db'

/** The subset of screen props the amend contract is assembled from. */
export interface ServiceAmendSource {
  addendums?: Addendum[]
  lockedValues?: Record<string, string | undefined>
  amending?: boolean
  onStartAmend?: () => void
  onDiscardAmend?: () => void
  onSaveAddendum?: (
    reason: string,
    changes: AddendumChange[],
  ) => void | Promise<void>
  issueSummary?: string
  /** Job equivalents of `issueSummary` and friends; absent on other types. */
  fault?: string
  deviceType?: string
  deviceDescription?: string
  peripherals?: string
  contactAddress?: string
  contactName?: string
  contactPhone?: string
  contactEmail?: string
  invoiceName?: string
  invoiceEmail?: string
  invoicePhone?: string
  invoiceAddress?: string
  invoiceVat?: string
  invoiceId?: string
}

/**
 * Assemble the shell's amend contract from a details screen's props plus the
 * live form draft.
 *
 * Returns `undefined` when the screen has no `onSaveAddendum` wired, which is
 * how {@link ServiceDetailsShell} decides between offering an amend flow and
 * falling back to unlock-by-status-change.
 */
export function useServiceAmend(
  props: ServiceAmendSource,
  amendDraft: Record<string, string>,
): ServiceAmendContract | undefined {
  const { onSaveAddendum } = props
  if (!onSaveAddendum) return undefined

  const currentValues: Record<string, string | undefined> = {
    issueSummary: props.issueSummary,
    fault: props.fault,
    deviceType: props.deviceType,
    deviceDescription: props.deviceDescription,
    peripherals: props.peripherals,
    contactAddress: props.contactAddress,
    contactName: props.contactName,
    contactPhone: props.contactPhone,
    contactEmail: props.contactEmail,
    invoiceName: props.invoiceName,
    invoiceEmail: props.invoiceEmail,
    invoicePhone: props.invoicePhone,
    invoiceAddress: props.invoiceAddress,
    invoiceVat: props.invoiceVat,
    invoiceId: props.invoiceId,
  }

  const amending = props.amending ?? false

  return {
    addendums: props.addendums ?? [],
    lockedValues: props.lockedValues ?? {},
    currentValues,
    amending,
    // Only diff while amending: outside that the form mirrors current values,
    // so any difference would be noise.
    pendingChanges: amending
      ? buildAddendumChanges(currentValues, amendDraft)
      : [],
    onStart: () => props.onStartAmend?.(),
    onDiscard: () => props.onDiscardAmend?.(),
    onSave: onSaveAddendum,
  }
}
